// Vertriebspartner-Reiter: Kooperationen, bei denen ein fremder Haendler unsere Ausbildungen
// im eigenen Namen verkauft. Erster Fall ist Marc Drescher (Massageliegenhaus), Modell
// "Gutschein": Er verkauft Gutscheincodes, der Kunde loest sie bei uns ein, wir stellen ihm
// je eingeloestem Code einen festen Betrag in Rechnung.
//
// Warum ein eigener Reiter und nicht eine Zeile im Awin-Reiter: Awin ist ein Netzwerk mit
// Provision NACH dem Verkauf zu unserem Preis. Hier ist es umgekehrt — der Partner bestimmt
// seinen Endkundenpreis selbst und schuldet uns einen festen Betrag. Die Zahl, die zaehlt,
// ist deshalb nicht die Provision, sondern die Partnermarge. Sie steht in jeder Zeile.
//
// ⚠️ Die Haekchenspalte bildet Anlage 1 des Kooperationsvertrages ab. Laut § 1 Abs. 2 ist ein
//    Kurs OHNE Haekchen nicht Gegenstand der Kooperation, auch wenn er in der Liste steht.
//    Deshalb steht die Zahl der ausgewaehlten Kurse gross oben und nicht im Kleingedruckten.
//
// Seit 02.11.2026 laeuft Massageliegenhaus im Modell "awin" (Uli): kein Gutschein, kein Vertrag.
// Marc empfiehlt als Awin-Publisher, der Kunde bucht und zahlt bei uns, Marc bekommt je Kurs eine
// feste Provision. Dann zaehlt nicht die Partnermarge, sondern was WIP nach Rabattcode, Provision
// und Awin-Gebuehr bleibt — die Tabelle rechnet je nach Modell anders, das Haekchen heisst
// "im Shop" und ist keine Vertragsanlage mehr.
import type { MarketingDeps } from "./marketing.js";

interface Partner {
  kennung: string; name: string; firma: string | null; ansprechpartner: string | null;
  anschrift: string | null; webseite: string | null; email: string | null; shop: string | null;
  modell: string; status: string; vertrag_stand: string | null;
  gueltigkeit_monate: number | null; einloese_pfad: string | null; notiz: string | null;
  awin_publisher_id: string | null; kunden_code: string | null; rabatt_prozent: number | null;
  unterlagen: string | null;
}
interface Angebot {
  partner: string; produkt_id: string; bezeichnung: string; kategorie: string;
  uvp: number; abrechnungsbetrag: number; gewaehlt: number;
  gtin: string | null; url: string | null; bild: string | null; hinweis: string | null;
  satz: string | null; provision: number | null; provision_hinweis: string | null;
}
interface Datei { name: string; breite: number; hoehe: number; format: string; url: string }
interface Satz { satz: string; dateien: Datei[] }
interface Antwort { partner: Partner[]; angebote: Angebot[]; saetze: Satz[] }

const STATUS_TEXT: Record<string, string> = {
  anbahnung: "in Anbahnung", aktiv: "aktiv", ruht: "ruht", beendet: "beendet",
};
const euro = (n: number): string => n.toLocaleString("de-DE", { minimumFractionDigits: 0 }) + " €";
const euroGanz = (n: number): string => Math.round(n).toLocaleString("de-DE") + " €";

// Was WIP im Modell "awin" von einer Buchung bleibt — dieselbe Rechnung wie beim Angebot an Marc
// (30.10.2026): Kundenpreis nach Rabattcode, minus Provision, minus Awin-Gebuehr 3,5 % vom Umsatz;
// auf Provision + Gebuehr vorsichtig 19 % USt, als waere sie nicht abziehbar.
const AWIN_GEBUEHR = 0.035;
function awinRechnung(a: Angebot, rabatt: number): { kunde: number; bleibt: number } {
  const kunde = a.uvp * (1 - rabatt / 100);
  const kosten = ((a.provision ?? 0) + kunde * AWIN_GEBUEHR) * 1.19;
  return { kunde, bleibt: kunde - kosten };
}
const MODELL_TEXT: Record<string, string> = {
  gutschein: "Gutscheinverkauf im eigenen Namen",
  awin: "Awin-Partnerprogramm mit Sonderkonditionen",
};

function kopfkarte(p: Partner, gewaehlt: number, gesamt: number,
  publisherSetzen: (wert: string) => void): HTMLElement {
  const box = document.createElement("div");
  box.className = "mp-block";

  const titel = document.createElement("h4");
  titel.className = "mp-saeule";
  titel.textContent = `${p.name}${p.ansprechpartner ? " · " + p.ansprechpartner : ""}`;
  const marke = document.createElement("span");
  marke.className = gewaehlt ? "mp-saeule__zahl mp-saeule__zahl--fertig" : "mp-saeule__zahl";
  marke.textContent = `${gewaehlt}/${gesamt} Kurse ${p.modell === "awin" ? "im Shop" : "gewählt"}`;
  titel.appendChild(marke);
  box.appendChild(titel);

  const awin = p.modell === "awin";
  const zeilen: Array<[string, string | null]> = awin ? [
    ["Modell", MODELL_TEXT.awin!],
    ["Status", STATUS_TEXT[p.status] ?? p.status],
    ["Vertrag", p.vertrag_stand],
    ["Kunden-Code", p.kunden_code ? `${p.kunden_code}${p.rabatt_prozent ? " · " + p.rabatt_prozent + " % Rabatt" : ""}` : "— keiner"],
    ["Unterlagen", p.unterlagen],
    ["Anschrift", p.anschrift],
    ["Webseite", p.webseite],
    ["Shopsystem", p.shop],
  ] : [
    ["Modell", MODELL_TEXT[p.modell] ?? p.modell],
    ["Status", STATUS_TEXT[p.status] ?? p.status],
    ["Vertrag", p.vertrag_stand],
    ["Gutschein gültig", p.gueltigkeit_monate ? `${p.gueltigkeit_monate} Monate` : "— noch nicht festgelegt"],
    // Eigene Adresse je Partner. Sie gehoert NICHT in den Produktstamm — dort stand sie
    // einmal und zeigte in allen 32 Zeilen auf eine Seite, die es nicht gibt.
    ["Einlöseadresse", p.einloese_pfad ? `wellnessinperfektion.de${p.einloese_pfad}` : "— noch nicht angelegt"],
    ["Anschrift", p.anschrift],
    ["Webseite", p.webseite],
    ["Shopsystem", p.shop],
  ];
  for (const [k, v] of zeilen) {
    if (!v) continue;
    const z = document.createElement("div");
    z.className = "mp-schritt__detail";
    z.textContent = `${k}: ${v}`;
    box.appendChild(z);
  }
  if (awin) {
    // Die Publisher-ID steckt in jedem Buchungslink. Ohne sie gibt es keinen Shopify-Import.
    const z = document.createElement("div");
    z.className = "mp-schritt__detail";
    const label = document.createElement("label");
    label.textContent = "Awin-Publisher-ID: ";
    const feld = document.createElement("input");
    feld.type = "text";
    feld.inputMode = "numeric";
    feld.size = 10;
    feld.value = p.awin_publisher_id ?? "";
    feld.placeholder = "nach Anmeldung";
    feld.setAttribute("aria-label", `Awin-Publisher-ID von ${p.name}`);
    feld.addEventListener("change", () => publisherSetzen(feld.value));
    label.appendChild(feld);
    z.appendChild(label);
    if (!p.awin_publisher_id) z.append(" — noch nicht bei Awin angemeldet");
    box.appendChild(z);
  }
  if (p.notiz) {
    const n = document.createElement("p");
    n.className = "modal-desc";
    n.textContent = p.notiz;
    box.appendChild(n);
  }
  return box;
}

function tabelle(
  p: Partner,
  angebote: Angebot[],
  umschalten: (a: Angebot) => void,
  gtinSetzen: (a: Angebot, wert: string) => void,
): HTMLElement {
  const awin = p.modell === "awin";
  const rabatt = p.rabatt_prozent ?? 0;
  // Kein Klassenname aus der Portal-CSS hier: die Suite kennt `table-scroll-wrap` nicht.
  // Der Wrapper scrollt die Tabelle in sich selbst, damit die GTIN-Spalte auf schmalen
  // Fenstern nicht das ganze Panel nach rechts schiebt.
  const wrap = document.createElement("div");
  wrap.style.overflowX = "auto";
  const t = document.createElement("table");
  t.className = "om-table";
  const kopf = document.createElement("thead");
  kopf.innerHTML = awin
    ? "<tr><th>Im Shop</th><th>Kurs</th><th>Kat.</th><th>Preis</th>"
      + `<th>Kunde zahlt${rabatt ? " (−" + rabatt + " %)" : ""}</th><th>Provision</th>`
      + "<th title=\"nach Rabattcode, Provision und Awin-Gebühr 3,5 %; Provision + Gebühr vorsichtig mit 19 % USt\">WIP behält</th><th>GTIN</th></tr>"
    : "<tr><th>Auswahl</th><th>Kurs</th><th>Kat.</th><th>UVP</th>"
      + "<th>an WIP</th><th>Partnermarge</th><th>GTIN</th></tr>";
  t.appendChild(kopf);
  const koerper = document.createElement("tbody");

  for (const a of angebote) {
    const tr = document.createElement("tr");
    if (!a.gewaehlt) tr.style.opacity = "0.5";

    const tdHaken = document.createElement("td");
    const haken = document.createElement("button");
    haken.className = "btn-secondary";
    haken.style.padding = "2px 10px";
    haken.textContent = a.gewaehlt ? "☑" : "☐";
    haken.title = awin
      ? (a.gewaehlt ? "Im Shopify-Import — klicken zum Entfernen" : "Nicht im Shopify-Import — klicken zum Aufnehmen")
      : (a.gewaehlt ? "Im Sortiment — klicken zum Entfernen" : "Nicht im Sortiment — klicken zum Aufnehmen");
    haken.setAttribute("aria-label", `${a.bezeichnung} ${a.gewaehlt ? "entfernen" : "aufnehmen"}`);
    haken.addEventListener("click", () => umschalten(a));
    tdHaken.appendChild(haken);

    const tdName = document.createElement("td");
    tdName.textContent = a.bezeichnung;
    if (a.hinweis) tdName.title = a.hinweis;

    const tdKat = document.createElement("td");
    tdKat.textContent = a.kategorie;

    const tdUvp = document.createElement("td");
    tdUvp.textContent = euro(a.uvp);

    const tdWip = document.createElement("td");
    const tdMarge = document.createElement("td");
    let tdBleibt: HTMLElement | null = null;
    if (awin) {
      const rech = awinRechnung(a, rabatt);
      tdWip.textContent = euroGanz(rech.kunde);
      tdMarge.textContent = a.provision != null ? euro(a.provision) + (a.provision_hinweis ? " ¹" : "") : "—";
      if (a.provision_hinweis) tdMarge.title = a.provision_hinweis;
      tdBleibt = document.createElement("td");
      tdBleibt.textContent = `≈ ${euroGanz(rech.bleibt)}`;
    } else {
      tdWip.textContent = euro(a.abrechnungsbetrag);
      const marge = a.uvp - a.abrechnungsbetrag;
      tdMarge.textContent = `${euro(marge)} · ${Math.round((marge / a.uvp) * 100)} %`;
    }

    const tdGtin = document.createElement("td");
    const feld = document.createElement("input");
    feld.type = "text";
    feld.value = a.gtin ?? "";
    feld.placeholder = "GTIN";
    feld.size = 15;
    feld.setAttribute("aria-label", `GTIN für ${a.bezeichnung}`);
    // Beim Verlassen speichern, nicht bei jedem Tastendruck: Eine halb getippte Nummer
    // waere sonst jedes Mal eine ungueltige GTIN und der Server lehnte sie ab.
    feld.addEventListener("change", () => gtinSetzen(a, feld.value));
    tdGtin.appendChild(feld);

    if (tdBleibt) tr.append(tdHaken, tdName, tdKat, tdUvp, tdWip, tdMarge, tdBleibt, tdGtin);
    else tr.append(tdHaken, tdName, tdKat, tdUvp, tdWip, tdMarge, tdGtin);
    koerper.appendChild(tr);
  }
  t.appendChild(koerper);
  wrap.appendChild(t);
  return wrap;
}

// Werbemittel je Kurs. Gezeigt werden nur Saetze zu Kursen, die im Sortiment des Partners
// stehen — ein Banner zu einem Kurs, den er gar nicht verkauft, ist nur Rauschen.
function werbemittelBlock(gewaehlt: Angebot[], saetze: Satz[]): HTMLElement | null {
  const meine = saetze.filter((x) => gewaehlt.some((a) => a.satz === x.satz));
  const ohne = gewaehlt.filter((a) => !a.satz);
  const box = document.createElement("div");
  box.className = "mp-block";

  const titel = document.createElement("h4");
  titel.className = "mp-saeule";
  titel.textContent = "Werbemittel für den Partner";
  const zahl = document.createElement("span");
  zahl.className = meine.length ? "mp-saeule__zahl mp-saeule__zahl--fertig" : "mp-saeule__zahl";
  zahl.textContent = `${meine.length} Sätze`;
  titel.appendChild(zahl);
  box.appendChild(titel);

  const hinweis = document.createElement("p");
  hinweis.className = "modal-desc";
  hinweis.textContent = "Die Adressen führen auf einen neutralen Pfad ohne Netzwerknamen — sie "
    + "landen im Quelltext des Partnershops und in seinen eigenen Werbemitteln. Die Bilder "
    + "tragen die EU-Kennzeichnung für KI-Inhalte, wo sie KI-erzeugt sind.";
  box.appendChild(hinweis);

  if (!gewaehlt.length) {
    const p = document.createElement("p");
    p.className = "modal-desc";
    p.textContent = "Sobald Kurse ausgewählt sind, stehen hier die passenden Sätze.";
    box.appendChild(p);
    return box;
  }

  for (const s of meine) {
    const h = document.createElement("div");
    h.className = "mp-schritt__titel";
    h.textContent = `${s.satz} · ${s.dateien.length} Formate`;
    box.appendChild(h);

    const liste = document.createElement("div");
    liste.style.overflowX = "auto";
    const t = document.createElement("table");
    t.className = "om-table";
    t.innerHTML = "<thead><tr><th>Format</th><th>Maße</th><th>Adresse</th></tr></thead>";
    const tb = document.createElement("tbody");
    for (const d of s.dateien) {
      const tr = document.createElement("tr");
      const f = document.createElement("td"); f.textContent = d.format;
      const m = document.createElement("td"); m.textContent = `${d.breite} × ${d.hoehe}`;
      const a = document.createElement("td");
      const link = document.createElement("a");
      link.href = d.url; link.target = "_blank"; link.rel = "noopener";
      link.textContent = d.url.replace("https://www.wellnessinperfektion.de", "");
      a.appendChild(link);
      tr.append(f, m, a);
      tb.appendChild(tr);
    }
    t.appendChild(tb);
    liste.appendChild(t);
    box.appendChild(liste);

    const kopieren = document.createElement("button");
    kopieren.className = "btn-secondary";
    kopieren.textContent = "⧉ Alle Adressen kopieren";
    kopieren.addEventListener("click", () => {
      void navigator.clipboard.writeText(s.dateien.map((d) => d.url).join("\n"))
        .then(() => { kopieren.textContent = "✓ kopiert"; window.setTimeout(() => { kopieren.textContent = "⧉ Alle Adressen kopieren"; }, 2000); });
    });
    box.appendChild(kopieren);
  }

  if (ohne.length) {
    const p = document.createElement("p");
    p.className = "modal-desc";
    p.textContent = `Ohne Bannersatz: ${ohne.map((a) => a.bezeichnung).join(", ")}. `
      + "Für diese Kurse gibt es bisher nur das Kursbild aus dem Shopify-Export.";
    box.appendChild(p);
  }
  return box;
}

export function initPartnerTab(modal: HTMLElement, deps: MarketingDeps): void {
  const panel = modal.querySelector<HTMLElement>('[data-tab-panel="partner"]');
  if (!panel) return;
  const host = panel.querySelector<HTMLElement>("#partner-inhalt");
  if (!host) return;
  const melden = (text: string): void => {
    const m = panel.querySelector<HTMLElement>('[data-msg="partner"]');
    if (m) m.textContent = text;
  };

  async function laden(): Promise<void> {
    host!.textContent = "Lade Partner …";
    const r = await deps.api<Antwort>("/api/marketing/partner");
    if (r.status !== 200 || !r.data) { host!.textContent = "Partner konnten nicht geladen werden."; return; }
    const d = r.data;
    host!.textContent = "";
    if (!d.partner.length) {
      host!.textContent = "Noch kein Partner hinterlegt — partner_seed.mjs ausführen.";
      return;
    }

    for (const p of d.partner) {
      const meine = d.angebote.filter((a) => a.partner === p.kennung);
      const gewaehlt = meine.filter((a) => a.gewaehlt).length;
      const awin = p.modell === "awin";
      const publisherSetzen = async (wert: string): Promise<void> => {
        const antwort = await deps.api<{ error?: string; detail?: string }>(
          "/api/marketing/partner/publisher", "POST", { partner: p.kennung, publisher_id: wert });
        if (antwort.status === 200) { melden(`Awin-Publisher-ID für ${p.name} gespeichert.`); await laden(); }
        else melden(`Publisher-ID abgelehnt: ${antwort.data?.detail ?? "ungültig"}.`);
      };
      host!.appendChild(kopfkarte(p, gewaehlt, meine.length, (w) => { void publisherSetzen(w); }));

      const umschalten = async (a: Angebot): Promise<void> => {
        const antwort = await deps.api("/api/marketing/partner/auswahl", "POST",
          { partner: a.partner, produkt_id: a.produkt_id, gewaehlt: !a.gewaehlt });
        if (antwort.status === 200) await laden();
        else melden("Auswahl konnte nicht gespeichert werden.");
      };
      const gtinSetzen = async (a: Angebot, wert: string): Promise<void> => {
        const antwort = await deps.api<{ error?: string; detail?: string }>(
          "/api/marketing/partner/gtin", "POST",
          { partner: a.partner, produkt_id: a.produkt_id, gtin: wert });
        melden(antwort.status === 200
          ? `GTIN für ${a.bezeichnung} gespeichert.`
          : `GTIN abgelehnt: ${antwort.data?.detail ?? "ungültig"} — erwartet werden 8, 12, 13 oder 14 Ziffern.`);
      };

      host!.appendChild(tabelle(p, meine, (a) => { void umschalten(a); }, (a, w) => { void gtinSetzen(a, w); }));

      // Summenzeile: Was bringt EIN Verkauf je Kategorie, und was steht ueberhaupt im Sortiment.
      const gewaehlteListe = meine.filter((a) => a.gewaehlt);
      const summe = document.createElement("p");
      summe.className = "modal-desc";
      if (awin) {
        const prov = gewaehlteListe.map((a) => a.provision ?? 0);
        const fussnote = meine.find((a) => a.provision_hinweis)?.provision_hinweis;
        summe.textContent = (gewaehlteListe.length
          ? `${gewaehlteListe.length} Kurse im Shopify-Import · Provision je Buchung ${euro(Math.min(...prov))} bis ${euro(Math.max(...prov))} (zzgl. MwSt.)`
          : "Kein Kurs im Shopify-Import.")
          + " · „WIP behält“ = Kundenpreis nach Code − Provision − Awin-Gebühr 3,5 %, Provision und Gebühr vorsichtig mit 19 % USt."
          + (fussnote ? ` ¹ ZFU: ${fussnote}.` : "");
      } else if (gewaehlteListe.length) {
        const min = Math.min(...gewaehlteListe.map((a) => a.abrechnungsbetrag));
        const max = Math.max(...gewaehlteListe.map((a) => a.abrechnungsbetrag));
        const ohne = gewaehlteListe.filter((a) => !a.gtin).length;
        summe.textContent = `${gewaehlteListe.length} Kurse im Sortiment · je eingelöstem Gutschein `
          + `${min === max ? euro(min) : euro(min) + " bis " + euro(max)} an WIP`
          + (ohne ? ` · ${ohne} davon noch ohne GTIN` : " · alle mit GTIN");
      } else {
        summe.textContent = "Noch kein Kurs ausgewählt. Solange kein Häkchen gesetzt ist, ist laut "
          + "§ 1 Abs. 2 des Kooperationsvertrages kein Kurs Gegenstand der Kooperation.";
      }
      host!.appendChild(summe);

      const knoepfe = document.createElement("div");
      knoepfe.className = "modal-actions inline";
      const csv = document.createElement("a");
      csv.className = "btn-secondary";
      csv.href = `/api/marketing/partner/${encodeURIComponent(p.kennung)}/shopify.csv`;
      csv.textContent = awin ? "⬇ Shopify-Import mit Buchungslink (CSV)" : "⬇ Shopify-Import (CSV)";
      csv.title = awin
        ? (!p.awin_publisher_id ? "Erst die Awin-Publisher-ID eintragen – sie steckt in jedem Buchungslink."
          : gewaehlteListe.length ? "Kurse „im Shop“ als Produkttyp „Online-Ausbildung“: unser Preis, Text, KI-Bild, GTIN, persönlicher Buchungslink und Kunden-Code."
          : "Kein Kurs im Shop — die Datei wäre leer.")
        : (gewaehlteListe.length
          ? "Enthält nur die ausgewählten Kurse. Der Verkaufspreis bleibt leer — den setzt der Partner selbst."
          : "Noch keine Kurse ausgewählt — die Datei wäre leer.");
      const gesperrt = !gewaehlteListe.length || (awin && !p.awin_publisher_id);
      if (gesperrt) {
        csv.setAttribute("aria-disabled", "true"); csv.style.opacity = "0.5";
        csv.addEventListener("click", (e) => { e.preventDefault(); melden(csv.title); });
      }
      knoepfe.appendChild(csv);
      host!.appendChild(knoepfe);

      const wm = werbemittelBlock(gewaehlteListe, d.saetze ?? []);
      if (wm) host!.appendChild(wm);
    }
  }

  const btn = modal.querySelector<HTMLButtonElement>('[data-tab="partner"]');
  let geladen = false;
  btn?.addEventListener("click", () => { if (!geladen) { geladen = true; void laden(); } });
  panel.querySelector<HTMLButtonElement>("#partner-neu-laden")?.addEventListener("click", () => { void laden(); });
}
